import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectService } from '../../services/projectService.js';
import { storage } from '../../utils/localStorage.js';
import { tokenManager } from '../../utils/tokenManager.js';
import OTPInput from '../auth/OTPInput.jsx';
import ProjectPasswordStrengthIndicator from '../common/ProjectPasswordStrengthIndicator.jsx';

const UpdateProjectPassword = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: request OTP, 2: verify OTP, 3: new password
  const [project, setProject] = useState(null);
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [otp, setOtp] = useState('');
  const [projectPasswordUpdateToken, setProjectPasswordUpdateToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');  
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);  useEffect(() => {
    // Check authentication
    const isAuth = tokenManager.isAuthenticated();
    
    if (!isAuth) {
      navigate('/login');
      return;
    }
    
    // Find project from localStorage
    const projects = storage.getProjects();
    const foundProject = projects.find(p => p.project_id === Number(projectId));
    
    if (foundProject) {
      setProject(foundProject);
    } else {
      setError('Project not found');
    }
  }, [projectId, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Handle OTP completion from OTPInput component
  const handleOTPComplete = (otpValue) => {
    setOtp(otpValue);
    setStep(3);
    setError('');
    setSuccess('');
  };

  const handleRequestUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {      
      // Check authentication
      const currentToken = storage.getJWT();
      if (!currentToken) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      const response = await projectService.requestProjectPasswordUpdate(Number(projectId));
      
      if (response.token) {
        setProjectPasswordUpdateToken(response.token);
        setStep(2);
        setSuccess('OTP sent to your email. Please check your inbox.');
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } catch (error) {
      setError(error.error || error.message || 'Failed to request password update');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    const validation = validatePassword(formData.newPassword);
    if (!validation.isValid) {
      setError(validation.errors.join('\n'));
      setLoading(false);
      return;
    }

    try {
      await projectService.completeProjectPasswordUpdate(
        projectPasswordUpdateToken,
        otp,
        formData.newPassword
      );
      setSuccess('Project password updated successfully!');
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Password update error:', error);
      
      let errorMessage = 'Failed to update project password. Please try again.';
      
      // Handle specific error cases - if OTP error, go back to step 2
      if (error.response?.status === 400) {
        if (error.response.data?.error?.includes('otp') || error.response.data?.error?.includes('OTP')) {
          setError('Invalid or expired OTP. Please re-enter the correct code.');
          setStep(2); // Go back to step 2 for OTP re-entry
          setLoading(false);
          return;
        } else if (error.response.data?.error?.includes('password') || error.response.data?.error?.includes('Password')) {
          errorMessage = 'Invalid password format. Password must be at least 8 characters with uppercase, lowercase, number, and special character.';
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.status === 410) {
        setError('OTP has expired. Please request a new code.');
        setStep(1); // Go back to step 1 for new OTP request
        setLoading(false);
        return;
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error occurred while updating password. Please try again later.';
      } else if (error.error) {
        errorMessage = error.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Password validation function
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="password-update-step">
            <div className="alert alert-info">
              <i className="fas fa-shield-alt"></i>
              An OTP will be sent to your registered email address.
            </div>
            <form onSubmit={handleRequestUpdate}>
              <div className="form-actions single-action">
                <button 
                  type="submit" 
                  className="btn btn-primary auth-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      Send OTP to Email
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        );

      case 2:
        return (
          <div className="password-update-step">
            <form onSubmit={(e) => e.preventDefault()}>
              <p className="step-description">
                Enter the 6-digit OTP sent to your email
              </p>
              
              <label htmlFor="otp">6-Digit OTP:</label>
              <OTPInput 
                onComplete={handleOTPComplete}
                disabled={loading}
              />
            </form>
          </div>
        );
      
      case 3:
        return (
          <div className="password-update-step">
            <form onSubmit={handleUpdatePassword}>
              {/* New Password */}
              <div className="form-group">
                <label htmlFor="newPassword">New Project Password:</label>
                <div className="password-input-container">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter new password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={toggleNewPasswordVisibility}
                    disabled={loading}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                <ProjectPasswordStrengthIndicator password={formData.newPassword} />
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password:</label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm new password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={toggleConfirmPasswordVisibility}
                    disabled={loading}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => setStep(2)}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  <i className="fas fa-arrow-left"></i>
                  Back
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary auth-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Updating Password...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-key"></i>
                      Update Project Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        );

      default:
        return (
          <div className="password-update-step">
            <div className="alert alert-error">
              <i className="fas fa-exclamation-triangle"></i>
              Something went wrong. Please try again.
            </div>
            <button 
              onClick={() => setStep(1)} 
              className="btn btn-secondary"
            >
              Start Over
            </button>
          </div>
        );
    }
  };

  if (!project) {
    return (
      <div className="container">
        <div className="loading">Loading project...</div>
      </div>
    );
  }

  return (
    <div className="container update-project-password-container">
      <div className="form-container">
        <div className="auth-header">
          <h2>🔒 Update Project Password</h2>
          <div className="step-indicator">
            Step {step} of 3
          </div>
          <p className="project-info">
            Project: <strong>{project.project_name}</strong>
          </p>
        </div>
        
        {error && (
          <div className="alert alert-error">
            <i className="fas fa-exclamation-triangle"></i>
            <span style={{ whiteSpace: 'pre-line' }}>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <i className="fas fa-check-circle"></i>
            {success}
          </div>
        )}

        {renderStep()}

        <div className="auth-actions">
          <button 
            type="button" 
            onClick={() => navigate('/dashboard')}
            className="btn btn-secondary"
            disabled={loading}
          >
            <i className="fas fa-arrow-left"></i>
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateProjectPassword;